import { BaseElement } from 'base';
import { bus } from 'bus';
import {
  search,
  classify,
  alternativeWords,
  mergeAlternatives,
  tryRows,
  normalizeTerm
} from 'search';
import { SEARCH_RESULTS_SECTION, SEARCH_COUNT_SECTION } from 'section-ids';
import { SEARCH_TOPIC } from 'search-topic';
import { sectionFragment } from 'section-fragment';

const HEADER_TAG = 'drawer-header';
const CLEAR_TAG = 'link-button';
const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const RESULTS_ID = 'search-panel-results';
const COUNT_ID = 'search-panel-count';
const RESULT_STATES = ['results', 'typo', 'alternatives'];
const RESULT_BANDS = ['suggestions', 'try', 'products', 'footer'];
const LINK_BANDS =
  "[data-search-band='products'], [data-search-band='footer'], [data-search-band='suggested']";
const RECENT = "[data-search-band='recent']";
const TRY = "[data-search-band='try']";
const EMPTY_STATES = ['no-match', 'alternatives'];
const ROW = 'li.list-row';

const BANDS = {
  default: ['recent', 'popular', 'suggested'],
  loading: ['loading'],
  results: ['suggestions', 'products', 'footer'],
  typo: ['message', 'suggestions', 'products', 'footer'],
  alternatives: ['message', 'try', 'products', 'footer'],
  'no-match': ['message', 'recent', 'popular', 'suggested'],
  unavailable: ['unavailable', 'suggested']
};

const FOCUSABLE = [
  "a[href]:not([tabindex='-1'])",
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])"
]
  .map(selector => `[data-search-panel] ${selector}`)
  .join(', ');

class SearchPanel extends BaseElement {
  #dialog = null;
  #panel = null;
  #input = null;
  #clearBox = null;
  #body = null;
  #status = null;
  #recentRows = null;
  #recentTemplate = null;
  #tryTemplate = null;
  #messageQuery = null;
  #noResultsText = null;
  #correctedText = null;
  #bands = {};
  #open = false;
  #opener = null;
  #connecting = false;
  #timer = 0;
  #frame = 0;
  #controller = null;
  #shownTerm = '';

  onConnect() {
    this.#dialog = this.q('[data-search-dialog]');
    this.#panel = this.q('[data-search-panel]');
    this.#input = this.q('[data-search-input]');
    this.#body = this.q('[data-search-body]');
    if (!this.#dialog || !this.#panel || !this.#input || !this.#body) {
      console.error(
        '[search-panel] missing dialog, panel, input or body:',
        this
      );
      return;
    }

    this.#clearBox = this.q('[data-search-clear]');
    this.#status = this.q('[data-search-status]');
    this.#recentRows = this.q(`${RECENT} [data-list-block-rows]`);
    this.#recentTemplate = this.q('[data-search-recent-template]');
    this.#tryTemplate = this.q('[data-search-try-template]');
    this.#messageQuery = this.q('[data-search-message-query]');
    this.#noResultsText = this.q("[data-search-message='no-results']");
    this.#correctedText = this.q("[data-search-message='corrected']");
    this.#bands = Object.fromEntries(
      this.qa('[data-search-band]').map(band => [band.dataset.searchBand, band])
    );

    this.listen(this.#dialog, 'cancel', this.#onCancel);
    this.listen(this.#dialog, 'close', this.#onDialogClose);
    this.listen(this, 'keydown', this.#onKeydown);
    this.listen(this.#input, 'input', this.#onInput);
    this.listen(this, 'click', this.#onClick);
    this.listen(this, 'shopify:block:select', this.#onBlockSelect);

    customElements.whenDefined(HEADER_TAG).then(() => {
      if (!this.isConnected) return;
      this.q(HEADER_TAG)?.run(() => this.requestDismiss());
    });
    customElements.whenDefined(CLEAR_TAG).then(() => {
      if (!this.isConnected) return;
      this.q(`[data-search-clear] ${CLEAR_TAG}`)?.run(() => this.#clear());
    });

    this.cleanup(() => {
      this.#cancel();
      cancelAnimationFrame(this.#frame);
    });

    this.#connecting = true;
    this.subscribe(SEARCH_TOPIC, this.#onActive);
    this.#connecting = false;
  }

  onDisconnect() {
    this.#cancel();
    if (this.#dialog?.open) this.#dialog.close();
    this.#open = false;
    this.#opener = null;
  }

  requestDismiss() {
    bus.publish(SEARCH_TOPIC, null);
  }

  #onActive(value) {
    if (value) this.#show();
    else this.#close();
  }

  #show() {
    if (this.#open) return;

    this.#open = true;
    const active = document.activeElement;
    if (active && active.localName !== 'body' && !this.contains(active))
      this.#opener = active;
    if (this.#connecting) this.dataset.instant = 'true';

    this.#reset();
    if (this.#dialog.open && !this.#dialog.matches(':modal'))
      this.#dialog.close();
    if (!this.#dialog.open) this.#dialog.showModal();
    this.#input.focus({ preventScroll: true });
  }

  #close() {
    if (!this.#open) return;

    this.#open = false;
    this.#cancel();
    if (this.#dialog.open) this.#dialog.close();
    this.#input.value = '';
    delete this.dataset.instant;

    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus?.();
  }

  #reset() {
    this.#cancel();
    this.#input.value = '';
    this.#setClearHidden(true);
    this.#shownTerm = '';
    this.#setMessage(false, '');
    for (const name of RESULT_BANDS) this.#fillBand(name);
    this.#renderRecent();
    this.#setState('default');
    this.#body.scrollTop = 0;
  }

  #cancel() {
    clearTimeout(this.#timer);
    this.#timer = 0;
    this.#controller?.abort();
    this.#controller = null;
    this.#body?.removeAttribute('aria-busy');
  }

  #onCancel(event) {
    event.preventDefault();
    this.requestDismiss();
  }

  #onDialogClose() {
    if (this.#open && !this.#dialog.open) this.requestDismiss();
  }

  #onBlockSelect() {
    bus.publish(SEARCH_TOPIC, true);
  }

  #term() {
    return normalizeTerm(this.#input.value);
  }

  #setClearHidden(hidden) {
    if (this.#clearBox) this.#clearBox.hidden = hidden;
  }

  #onInput() {
    this.#setClearHidden(this.#input.value === '');
    const term = this.#term();
    if (term.length < MIN_CHARS) {
      this.#cancel();
      this.#shownTerm = '';
      if (this.dataset.state !== 'default') this.#setState('default');
      return;
    }
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#run(term), DEBOUNCE_MS);
  }

  #onKeydown(event) {
    if (event.key === 'Escape') {
      if (event.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      this.requestDismiss();
      return;
    }
    if (event.key === 'Enter' && event.target === this.#input) {
      if (event.isComposing) return;
      const term = this.#term();
      if (term.length < MIN_CHARS) return;
      event.preventDefault();
      clearTimeout(this.#timer);
      this.#remember(term);
      this.#run(term);
      return;
    }
    if (event.key === 'Tab') this.#trapTab(event);
  }

  #trapTab(event) {
    event.stopPropagation();
    const focusable = this.qa(FOCUSABLE).filter(
      element => element.offsetParent !== null && !element.closest('[inert]')
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !focusable.includes(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !focusable.includes(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  #onClick(event) {
    const target = event.target;
    if (target.closest('[data-search-scrim]')) {
      this.requestDismiss();
      return;
    }

    const remove = target.closest('[data-list-row-remove]');
    if (remove) {
      this.#forget(remove);
      return;
    }

    const row = target.closest('[data-list-row-value]');
    if (row) {
      this.#pick(row.dataset.listRowValue);
      return;
    }

    if (this.#shownTerm && target.closest('a[href]')?.closest(LINK_BANDS))
      this.#remember(this.#shownTerm);
  }

  #pick(value) {
    if (!value) return;
    this.#input.value = value;
    this.#setClearHidden(false);
    this.#input.focus({ preventScroll: true });
    clearTimeout(this.#timer);
    this.#remember(value);
    this.#run(this.#term());
  }

  #forget(button) {
    const selector = `${RECENT} [data-list-row-remove]`;
    const index = this.qa(selector).indexOf(button);
    search.forget(button.dataset.listRowRemove ?? '');
    this.#refreshRecent();
    const removes = this.qa(selector);
    const next = removes[index] ?? removes[index - 1] ?? this.#input;
    next.focus({ preventScroll: true });
  }

  #remember(term) {
    search.remember(term);
    this.#refreshRecent();
  }

  #refreshRecent() {
    this.#renderRecent();
    this.#setState(this.dataset.state);
  }

  #renderRecent() {
    const proto = this.#templateNode(this.#recentTemplate, ROW);
    if (!proto) return;

    const terms = search.recent();
    if (!this.#renderRows(RECENT, proto, terms)) return;

    const labels = this.qa(`${RECENT} [data-list-row-label]`);
    const removes = this.qa(`${RECENT} [data-list-row-remove]`);
    terms.forEach((term, index) => {
      const id = `search-panel-recent-${index}`;
      labels[index].id = id;
      removes[index].dataset.listRowRemove = term;
      removes[index].setAttribute('aria-describedby', id);
    });
  }

  #renderTry(terms) {
    this.#fillBand('try');
    const block = this.#templateNode(this.#tryTemplate, '.list-block');
    const proto = this.#templateNode(this.#tryTemplate, ROW);
    if (!block || !proto || terms.length === 0) return;

    this.#bands.try?.replaceChildren(block.cloneNode(true));
    this.#renderRows(TRY, proto, terms);
  }

  #renderRows(band, proto, terms) {
    const rows = this.q(`${band} [data-list-block-rows]`);
    if (!rows) return false;

    rows.replaceChildren(...terms.map(() => proto.cloneNode(true)));
    const controls = this.qa(`${band} [data-list-row-value]`);
    const labels = this.qa(`${band} [data-list-row-label]`);
    terms.forEach((term, index) => {
      controls[index].dataset.listRowValue = term;
      labels[index].textContent = term;
    });
    return true;
  }

  #templateNode(template, selector) {
    return [...(template?.content.children ?? [])].find(node =>
      node.matches(selector)
    );
  }

  #isCurrent(controller) {
    return (
      this.isConnected &&
      this.#controller === controller &&
      !controller.signal.aborted
    );
  }

  async #run(term) {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const { signal } = controller;

    if (!RESULT_STATES.includes(this.dataset.state)) {
      this.#setState('loading');
      this.#announce('loading');
    }
    this.#body.setAttribute('aria-busy', 'true');

    try {
      const original = this.#parse(await search.query(term, { signal }));
      if (!this.#isCurrent(controller)) return;

      let shown = original;
      let verdict = classify(original.facts(term));
      let messageTerm = term;

      if (verdict.state === 'typo') {
        const correction = verdict.correction;
        let corrected = null;
        try {
          corrected = this.#parse(await search.query(correction, { signal }));
        } catch (error) {
          if (!this.#isCurrent(controller)) return;
          console.error('[search-panel] correction failed:', error);
        }
        if (!this.#isCurrent(controller)) return;

        const second = corrected
          ? classify(corrected.facts(correction), { shouldCorrect: false })
          : null;
        if (second?.state === 'results') {
          shown = corrected;
          verdict = { ...verdict, suggestions: second.suggestions };
          messageTerm = correction;
        } else {
          verdict = classify(original.facts(term), { shouldCorrect: false });
        }
      }

      let tryTerms = tryRows(shown.tries, term);
      if (EMPTY_STATES.includes(verdict.state)) {
        const words = alternativeWords(term);
        const parsed = await Promise.all(
          words.map(word => this.#queryWord(word, controller))
        );
        if (!this.#isCurrent(controller)) return;

        const merged = mergeAlternatives(
          parsed.map((entry, index) => entry?.facts(words[index]) ?? null),
          { query: term, base: original.tries }
        );
        tryTerms = merged.rows;
        const keepsOriginal =
          verdict.state === 'alternatives' && original.hasProducts;
        if (merged.first !== -1 && !keepsOriginal) {
          shown = await this.#withCount(
            parsed[merged.first],
            words[merged.first],
            controller
          );
          if (!this.#isCurrent(controller)) return;
          verdict = {
            state: 'alternatives',
            correction: null,
            suggestions: []
          };
        }
      }

      this.#render(term, shown, verdict, messageTerm, tryTerms);
    } catch (error) {
      if (!this.#isCurrent(controller)) return;
      console.error('[search-panel] search failed:', error);
      this.#shownTerm = '';
      this.#setState('unavailable');
      this.#announce('unavailable');
    } finally {
      if (this.#controller === controller && this.isConnected)
        this.#body.removeAttribute('aria-busy');
    }
  }

  async #queryWord(word, controller) {
    try {
      return this.#parse(
        await search.query(word, {
          signal: controller.signal,
          withCount: false
        })
      );
    } catch (error) {
      if (this.#isCurrent(controller))
        console.error('[search-panel] word search failed:', error);
      return null;
    }
  }

  async #withCount(entry, word, controller) {
    if (!entry.hasProducts) return entry;
    try {
      const sections = await search.count(word, { signal: controller.signal });
      return { ...entry, count: this.#count(sections) };
    } catch {
      return entry;
    }
  }

  #count(sections) {
    return sectionFragment(sections, SEARCH_COUNT_SECTION, COUNT_ID);
  }

  #parse(sections) {
    const results = sectionFragment(
      sections,
      SEARCH_RESULTS_SECTION,
      RESULTS_ID
    );
    if (!results) throw new Error('[search-panel] results section missing');
    const count = this.#count(sections);
    const slot = name => results.querySelector(`[data-search-slot='${name}']`);

    const queries = [...(slot('suggestions')?.querySelectorAll(ROW) ?? [])].map(
      row => ({
        text:
          row.querySelector('[data-list-row-value]')?.dataset.listRowValue ??
          '',
        marked: row.dataset.marked === 'true'
      })
    );
    const collections = [
      ...(slot('collections')?.querySelectorAll('[data-search-collection]') ??
        [])
    ].map(item => item.textContent.trim());
    const products = [...results.querySelectorAll('[data-search-product]')].map(
      item => item.dataset.words ?? ''
    );
    const tries = [
      ...(slot('try')?.querySelectorAll('[data-list-row-value]') ?? [])
    ].map(control => control.dataset.listRowValue ?? '');

    return {
      slot,
      count,
      tries,
      hasProducts: products.length > 0,
      facts: query => ({ query, queries, collections, products })
    };
  }

  #render(term, shown, verdict, messageTerm, tryTerms) {
    for (const name of ['suggestions', 'products'])
      this.#fillBand(name, shown.slot(name));
    this.#renderTry(tryTerms);

    this.qa(`[data-search-band='suggestions'] ${ROW}`).forEach((row, index) => {
      row.hidden = !verdict.suggestions.includes(index);
    });

    this.#fillBand(
      'footer',
      shown.count?.querySelector('[data-search-count-action]')
    );

    const state = this.#resolveState(verdict.state);
    const isTypo = state === 'typo';
    this.#setMessage(isTypo, isTypo ? messageTerm : term);
    this.#shownTerm = term;

    this.#setState(state);
    this.#body.scrollTop = 0;
    this.#announce(
      state,
      shown.count?.querySelector('[data-search-count-status]')?.textContent
    );
  }

  #resolveState(state) {
    const hasProducts = this.#hasContent('products');
    const isEmpty = !hasProducts && !this.#hasContent('suggestions');
    const resolved =
      isEmpty && ['results', 'typo'].includes(state) ? 'alternatives' : state;
    const isBare =
      resolved === 'alternatives' && !hasProducts && !this.#hasContent('try');
    return isBare ? 'no-match' : resolved;
  }

  #fillBand(name, source) {
    const band = this.#bands[name];
    if (!band) return;
    if (band.contains(document.activeElement))
      this.#input.focus({ preventScroll: true });
    band.replaceChildren(...(source?.childNodes ?? []));
  }

  #setMessage(isTypo, query) {
    if (this.#noResultsText) this.#noResultsText.hidden = isTypo;
    if (this.#correctedText) this.#correctedText.hidden = !isTypo;
    if (this.#messageQuery) this.#messageQuery.textContent = query;
  }

  #clear() {
    this.#cancel();
    this.#input.value = '';
    this.#shownTerm = '';
    this.#setState('default');
    this.#input.focus();
    this.#setClearHidden(true);
  }

  #setState(state) {
    const current = BANDS[state] ? state : 'default';
    this.dataset.state = current;
    const shown = BANDS[current];
    for (const [name, band] of Object.entries(this.#bands)) {
      band.hidden = !(shown.includes(name) && this.#hasContent(name));
    }
  }

  #hasContent(name) {
    const band = `[data-search-band='${name}']`;
    switch (name) {
      case 'recent':
        return (this.#recentRows?.children.length ?? 0) > 0;
      case 'suggestions':
        return this.qa(`${band} ${ROW}:not([hidden])`).length > 0;
      case 'try':
        return this.qa(`${band} ${ROW}`).length > 0;
      case 'products':
        return this.qa(`${band} [data-search-product]`).length > 0;
      case 'footer':
        return this.#bands.footer.children.length > 0;
      default:
        return true;
    }
  }

  #announce(state, countText) {
    if (!this.#status) return;
    const text = this.#announcement(state, countText);
    this.#status.textContent = '';
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (this.#status) this.#status.textContent = text;
    });
  }

  #announcement(state, countText) {
    const read = element => normalizeTerm(element?.textContent);
    switch (state) {
      case 'loading':
        return read(this.#bands.loading);
      case 'results':
        return countText?.trim() || read(this.q('[data-search-updated]'));
      case 'typo':
        return `${read(this.#correctedText)} ${read(this.#messageQuery)}`;
      case 'alternatives':
      case 'no-match':
        return `${read(this.#noResultsText)} ${read(this.#messageQuery)}`;
      case 'unavailable':
        return read(this.#bands.unavailable);
      default:
        return '';
    }
  }
}

if (!customElements.get('search-panel'))
  customElements.define('search-panel', SearchPanel);
